import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarClock, User, UserCog, Users, RefreshCw, BarChart3, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { resetService } from '@/services/resetService';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

// Type definitions for dashboard data
interface DashboardStats {
  appointmentCount: number;
  patientCount: number;
  doctorCount: number;
  appointmentGrowth: number;
  patientGrowth: number;
  doctorGrowth: number;
  recentAppointments: any[];
}

const Dashboard: React.FC = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState<DashboardStats>({
    appointmentCount: 0,
    patientCount: 0,
    doctorCount: 0,
    appointmentGrowth: 0,
    patientGrowth: 0,
    doctorGrowth: 0,
    recentAppointments: [],
  });
  
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      try {
        // Get current date ranges
        const now = new Date();
        const currentMonthStart = startOfMonth(now);
        const currentMonthEnd = endOfMonth(now);
        
        // Get previous month date ranges
        const prevMonthStart = startOfMonth(subMonths(now, 1));
        const prevMonthEnd = endOfMonth(subMonths(now, 1));
        
        // Format dates for Supabase queries
        const currentStartStr = format(currentMonthStart, 'yyyy-MM-dd');
        const currentEndStr = format(currentMonthEnd, 'yyyy-MM-dd');
        const prevStartStr = format(prevMonthStart, 'yyyy-MM-dd');
        const prevEndStr = format(prevMonthEnd, 'yyyy-MM-dd');
        
        console.log("Date ranges:", { 
          currentMonth: `${currentStartStr} to ${currentEndStr}`, 
          prevMonth: `${prevStartStr} to ${prevEndStr}` 
        });
        
        // Get current month appointment count
        const { data: currentAppointments, error: currentAppError } = await supabase
          .from('appointments')
          .select('id, date, time_slot, status, doctor_id, patient_id')
          .gte('date', currentStartStr)
          .lte('date', currentEndStr);
        
        if (currentAppError) throw currentAppError;
        
        // Get previous month appointment count
        const { data: prevAppointments, error: prevAppError } = await supabase
          .from('appointments')
          .select('id')
          .gte('date', prevStartStr)
          .lte('date', prevEndStr);
          
        if (prevAppError) throw prevAppError;
        
        // Get current month patient registrations
        const { data: currentPatients, error: currentPatientError } = await supabase
          .from('patients')
          .select('id')
          .gte('created_at', currentStartStr)
          .lte('created_at', currentEndStr);
        
        if (currentPatientError) throw currentPatientError;
        
        // Get previous month patient registrations
        const { data: prevPatients, error: prevPatientError } = await supabase
          .from('patients')
          .select('id')
          .gte('created_at', prevStartStr)
          .lte('created_at', prevEndStr);
          
        if (prevPatientError) throw prevPatientError;
        
        // Get current month doctor registrations
        const { data: currentDoctors, error: currentDoctorError } = await supabase
          .from('doctors')
          .select('id')
          .gte('created_at', currentStartStr)
          .lte('created_at', currentEndStr);
        
        if (currentDoctorError) throw currentDoctorError;
        
        // Get previous month doctor registrations
        const { data: prevDoctors, error: prevDoctorError } = await supabase
          .from('doctors')
          .select('id')
          .gte('created_at', prevStartStr)
          .lte('created_at', prevEndStr);
          
        if (prevDoctorError) throw prevDoctorError;
        
        // Get total counts
        const { count: totalPatientCount, error: totalPatientError } = await supabase
          .from('patients')
          .select('id', { count: 'exact', head: true });
          
        if (totalPatientError) throw totalPatientError;
        
        // Get total doctor count
        const { count: totalDoctorCount, error: totalDoctorError } = await supabase
          .from('doctors')
          .select('id', { count: 'exact', head: true });
          
        if (totalDoctorError) throw totalDoctorError;
        
        // Calculate growth rates
        const calculateGrowthRate = (current: number, previous: number) => {
          if (previous === 0) return current > 0 ? 100 : 0; // If previous was 0, show 100% growth if current > 0
          return Math.round(((current - previous) / previous) * 100);
        };
        
        const currentAppCount = currentAppointments?.length || 0;
        const prevAppCount = prevAppointments?.length || 0;
        const currentPatientCount = currentPatients?.length || 0;
        const prevPatientCount = prevPatients?.length || 0;
        const currentDoctorCount = currentDoctors?.length || 0;
        const prevDoctorCount = prevDoctors?.length || 0;
        
        console.log("Monthly counts:", {
          appointments: { current: currentAppCount, previous: prevAppCount },
          patients: { current: currentPatientCount, previous: prevPatientCount },
          doctors: { current: currentDoctorCount, previous: prevDoctorCount },
        });
        
        const appointmentGrowth = calculateGrowthRate(currentAppCount, prevAppCount);
        const patientGrowth = calculateGrowthRate(currentPatientCount, prevPatientCount);
        const doctorGrowth = calculateGrowthRate(currentDoctorCount, prevDoctorCount);
        
        // Get all appointments for the recent appointments section
        const { data: appointments, error: appointmentError } = await supabase
          .from('appointments')
          .select('id, date, time_slot, status, doctor_id, patient_id')
          .order('date', { ascending: false });
          
        if (appointmentError) throw appointmentError;
        
        // Get recent appointments with doctor and patient details
        const recentAppointments = [];
        
        if (appointments && appointments.length > 0) {
          // Get the 4 most recent appointments
          const latestAppointments = appointments.slice(0, 4);
          
          // Fetch doctor and patient details for these appointments
          for (const appointment of latestAppointments) {
            const [doctorResult, patientResult] = await Promise.all([
              supabase
                .from('doctors')
                .select('name, specialty')
                .eq('id', appointment.doctor_id)
                .single(),
                
              supabase
                .from('patients')
                .select('full_name')
                .eq('id', appointment.patient_id)
                .single()
            ]);
            
            recentAppointments.push({
              appointment: appointment,
              doctor: doctorResult.data,
              patient: patientResult.data
            });
          }
        }
        
        return {
          appointmentCount: appointments ? appointments.length : 0,
          patientCount: totalPatientCount || 0,
          doctorCount: totalDoctorCount || 0,
          appointmentGrowth,
          patientGrowth,
          doctorGrowth,
          recentAppointments
        };
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        throw error;
      }
    }
  });
  
  useEffect(() => {
    if (dashboardData) {
      setStats(dashboardData);
    }
  }, [dashboardData]);
  
  const handleResetMockData = async () => {
    try {
      toast({
        title: "Resetting Data",
        description: "Attempting to reset doctor and patient data...",
      });
      
      await resetService.resetMockData();
      
      toast({
        title: "Data Reset Successful",
        description: "All doctor and patient data has been reset. Staff data remains intact.",
      });
    } catch (error) {
      console.error('Error resetting mock data:', error);
      toast({
        title: "Data Reset Failed",
        description: "There was an error resetting the data. Please check console for details.",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-lg">Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <h2 className="text-lg font-medium text-red-700">Error loading dashboard data</h2>
        <p className="text-sm text-red-600">Please check your database connection.</p>
        <Button 
          variant="outline" 
          className="mt-4 flex items-center gap-2"
          onClick={handleResetMockData}
        >
          <RefreshCw className="h-4 w-4" />
          Reset Data & Try Again
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <Button 
          variant="outline" 
          className="flex items-center gap-2"
          onClick={handleResetMockData}
        >
          <RefreshCw className="h-4 w-4" />
          Reset Doctor & Patient Data
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Appointments</CardTitle>
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.appointmentCount}</div>
            <p className="text-xs text-muted-foreground">
              {stats.appointmentGrowth > 0 ? '+' : ''}{stats.appointmentGrowth}% compared to last month
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Patients</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.patientCount}</div>
            <p className="text-xs text-muted-foreground">
              {stats.patientGrowth > 0 ? '+' : ''}{stats.patientGrowth}% compared to last month
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Doctors</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.doctorCount}</div>
            <p className="text-xs text-muted-foreground">
              {stats.doctorGrowth > 0 ? '+' : ''}{stats.doctorGrowth}% compared to last month
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="overview" className="mt-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="appointments">Appointments</TabsTrigger>
          <TabsTrigger value="patients">Patients</TabsTrigger>
          <TabsTrigger value="doctors">Doctors</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Weekly Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80 flex items-center justify-center border-2 border-dashed rounded-lg">
                  <p className="text-muted-foreground">Weekly statistics chart will be implemented soon</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Upcoming Appointments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.recentAppointments.length > 0 ? (
                  <div className="space-y-4">
                    {stats.recentAppointments.map((item, i) => (
                      <div key={item.appointment.id} className="flex justify-between items-center border-b pb-2">
                        <div>
                          <p className="font-medium">{item.patient?.full_name || 'Unknown Patient'}</p>
                          <p className="text-sm text-muted-foreground">
                            Dr. {item.doctor?.name || 'Unknown'} • {item.doctor?.specialty || 'Checkup'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{new Date(item.appointment.date).toLocaleDateString()}</p>
                          <p className="text-sm text-muted-foreground">{item.appointment.time_slot}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <p className="text-muted-foreground">No upcoming appointments</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="appointments">
          <div className="h-[400px] flex items-center justify-center border-2 border-dashed rounded-lg mt-4">
            <p className="text-muted-foreground">Appointment details will be implemented in future updates</p>
          </div>
        </TabsContent>
        <TabsContent value="patients">
          <div className="h-[400px] flex items-center justify-center border-2 border-dashed rounded-lg mt-4">
            <p className="text-muted-foreground">Patient statistics will be implemented in future updates</p>
          </div>
        </TabsContent>
        <TabsContent value="doctors">
          <div className="h-[400px] flex items-center justify-center border-2 border-dashed rounded-lg mt-4">
            <p className="text-muted-foreground">Doctor statistics will be implemented in future updates</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
